import { useEffect, useState } from 'react';

import BpkCardList from '@skyscanner-internal/backpack-web/bpk-component-card-list';
import {
  ACCESSORY_DESKTOP_TYPES,
  LAYOUTS,
} from '@skyscanner-internal/backpack-web/bpk-component-card-list/src/common-types';
import {
  EXPERIMENT_CHOKE_POINT_MINIEVENT_NAME,
  EXPERIMENT_CHOKE_POINT_SCHEMA_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classnames from 'classnames';

import { logMiniEvent } from '../../logMiniEvent';
import { useMBDContext } from '../common/MBD/useMBDContext';

import DestinationInfoCard from './DestinationInfoCard';

import type { LayoutDesktop as BpkLayoutDesktop } from '@skyscanner-internal/backpack-web/bpk-component-card-list/src/common-types';
import type { SkyscannerContentapiV2ComponentsDestinationInfoCards } from '@skyscanner-internal/landing-pages-api-web-http-client';
import type { DestinationInfoCardsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DestinationInfoCardsProps';

import STYLES from './DestinationInfoCards.module.scss';

const LAYOUT_DESKTOP_BPK_MAP: Record<
  SkyscannerContentapiV2ComponentsDestinationInfoCards.DestinationInfoCardsLayoutDesktop,
  BpkLayoutDesktop
> = {
  LAYOUT_DESKTOP_GRID: LAYOUTS.grid,
  LAYOUT_DESKTOP_ROW: LAYOUTS.row,
  // Default to grid if unspecified
  LAYOUT_DESKTOP_UNSPECIFIED: LAYOUTS.grid,
};

const HOTELS_DESTINATION_INFO_CARDS_ELIGIBLE =
  'hotels_destination_info_cards_eligible';

const DestinationInfoCards = ({
  cards,
  layoutDesktop = 'LAYOUT_DESKTOP_GRID',
  title,
}: DestinationInfoCardsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  // BpkCardList's internal BpkBreakpoint picks desktop on SSR (it doesn't
  // accept matchSSR). On a mobile viewport this leaves the static grid stuck
  // until a resize/orientation event triggers a re-render. Flip a key after
  // hydration so BpkCardList remounts once and its internal BpkBreakpoint
  // reads the real viewport. Same workaround as HighlightedArticles.
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);

    // For the experiment, we want to log a mini event when the component is eligible to be rendered.
    if (cards?.length) {
      logMiniEvent(
        EXPERIMENT_CHOKE_POINT_MINIEVENT_NAME,
        EXPERIMENT_CHOKE_POINT_SCHEMA_NAME,
        {
          name: HOTELS_DESTINATION_INFO_CARDS_ELIGIBLE,
          experiments: [
            {
              experiment_name: 'HIC_DestinationInfoCards',
            },
          ],
        },
      );
    }
  }, []);

  if (!cards?.length) {
    return null;
  }

  const reserveSpace =
    !isClient && LAYOUT_DESKTOP_BPK_MAP[layoutDesktop] === LAYOUTS.grid;

  return (
    <div
      className={classnames(STYLES.DestinationInfoCards, {
        [STYLES['DestinationInfoCards--reserveSpace']]: reserveSpace,
      })}
      data-testid="destinationInfoCards"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      {reserveSpace && (
        <noscript>
          <style>{`.${STYLES['DestinationInfoCards--reserveSpace']} * { visibility: visible !important; }`}</style>
        </noscript>
      )}
      <BpkCardList
        key={isClient ? 'client-key' : 'server-key'}
        title={title || ''}
        cardList={cards.map((card, cardIndex) => (
          <DestinationInfoCard
            key={card.title}
            cardIndex={cardIndex}
            {...card}
          />
        ))}
        layoutMobile={LAYOUTS.rail}
        layoutDesktop={LAYOUT_DESKTOP_BPK_MAP[layoutDesktop]}
        {...(layoutDesktop === 'LAYOUT_DESKTOP_ROW' && {
          accessoryDesktop: ACCESSORY_DESKTOP_TYPES.pagination,
        })}
      />
    </div>
  );
};

export default DestinationInfoCards;
